/**
 * Created by Daniel on 1/13/2020
 * -------------------------------------
 */

import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../redux';
import { workflowGetTaskByStatusSelector, workflowSetTaskStatusSelector, workflowSetTaskProcessorSelector } from '../../../../tamalelibs/redux/reducers/workflow-task.reducer';
import { OperationType, QuickTask, SubTask, TaskItem, Processor, TaskStatusFilter } from '../../../../tamalelibs/models/workflow.model';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LONG_TOUCH_INTERVAL } from '../../../../tamalelibs/constants/business.constants';
import { WorkflowTaskService } from '../../../../tamalelibs/services/workflow-task.service';
import { TaskMenuConfig } from '../task-menu/task-menu.model';
import { WorkflowTaskActionTypes } from '../../../../tamalelibs/redux/actions/workflow-task.actions';
import { StoreQuerierService } from '../../../../services/store-querier.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../../widgets/tam-context-menu/tam-context-menu.model';
import { AlertButton, AlertBtnTypes } from '../../../../widgets/alert-window/alert-window.model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';

export const TASK_STATUS_MENU_TEXT = ['Show Ongoing Tasks', 'Show Closed Tasks'];
export const TASK_PROCESSOR_MENU_TEXT = ['Assigned To Me', 'Created By Me'];

@Component({
    selector: 'task-list',
    templateUrl: './task-list.component.html',
    styleUrls: ['./task-list.component.scss']
})
export class TaskListComponent implements OnInit, OnDestroy {
    contextMenuConfig: ContextMenuConfig;
    onePageTasks = new Array<TaskItem>();
    scrollDownDistance = 1;
    scrollUpDistance = 1;
    selectItem = new TaskItem();
    taskStatusConfig = new TaskMenuConfig(0, TASK_STATUS_MENU_TEXT); // 0: default selected index; TASK_STATUS_MENU_TEXT: context menu items
    taskProcessorConfig = new TaskMenuConfig(0, TASK_PROCESSOR_MENU_TEXT); // 0: default selected index; TASK_PROCESSOR_MENU_TEXT: context menu items
    throttle = 150;
    showFilter = false;

    private _destroySubscriptions: Array<Subscription> = [];
    private _isIPAD = false;
    private _numOnceGetFromTaskList = 20;
    private _numShowInTaskListContainer = 40;
    private _processor = 0;
    private _status = 0;
    private _taskList: Array<TaskItem>;
    private _touchStart: number;

    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _deviceService: DeviceDetectorService,
        private _slideSheetService: SlideSheetService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
        private _workflowTaskService: WorkflowTaskService) {
        this._isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
    }

    ngOnInit() {
        this._initControls();
        this._destroySubscriptions.push(
            this._store.pipe(
                select(workflowGetTaskByStatusSelector)
            ).subscribe(taskList => this._onSubscribeTaskList(taskList)),
            this.taskStatusConfig.feedbackSubject$.subscribe(event => this._taskStatusEventHandler(event)),
            this.taskProcessorConfig.feedbackSubject$.subscribe(event => this._taskProcessorEventHandler(event)),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onCreateQuickTask(event) {
        this._slideSheetService.showSlideSheetForQuickTask(event, OperationType.Create, null, null,
            {
                subject: this._slideSheetService.slideSheetActionSubject$,
                action: {
                    type: SlideSheetActionTypes.TASK_LIST,
                    payload: 'AutoChange',
                },
            }
        );
    }

    onContextMenu(event, task) {
        if (this._status === TaskStatusFilter.Closed) {
            this.selectItem = task;
            this._changeDetectorRef.detectChanges();
            this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, task));
        }
    }

    onItemSelect(event, taskItem: TaskItem) {
        if (taskItem) {
            this.selectItem = taskItem;
            this._workflowTaskService.getQuickTaskDefinitionById(taskItem.definitionId).subscribe(quickTask => {
                const task = QuickTask.parse(quickTask);
                const subTask = new SubTask();
                subTask.subTaskId = taskItem.subTaskId;
                subTask.status = taskItem.status;
                subTask.remindDate = taskItem.remindDate;
                this._slideSheetService.showSlideSheetForQuickTask(event, OperationType.Edit, task, subTask,
                    {
                        subject: this._slideSheetService.slideSheetActionSubject$,
                        action: {
                            type: SlideSheetActionTypes.TASK_LIST,
                            payload: 'AutoChange',
                        },
                    }
                );
            });
        }
    }

    onScrolledDown(event) {
        let startIndex = 0;
        // set the start index to get from all task items
        if (this.onePageTasks.length > 0) {
            startIndex = this._taskList.findIndex(task => task.id === this.onePageTasks[this.onePageTasks.length - 1].id);
            startIndex = startIndex + 1;
        }
        // get the fixed number items from all task items. this._numOnceGetFromTaskList = 20
        const tmp = this._taskList.slice(startIndex, startIndex + this._numOnceGetFromTaskList);
        // stop to push the data to task list ui container if scroll to the end
        if (this.onePageTasks[this.onePageTasks.length - 1].id !== this._taskList[this._taskList.length - 1].id) {
            tmp.forEach(item => {
                // keep the task ui container size as this._numShowInTaskListContainer = 40
                if (this.onePageTasks.length === this._numShowInTaskListContainer) {
                    // remove one item from the top if the number of the container is more than this._numShowInTaskListContainer = 40
                    this.onePageTasks.shift();
                }
                // push one item to the end after remove one
                this.onePageTasks.push(item);
            });
        }
    }

    onScrolledUp(event) {
        let endIndex = 0;
        // set the end index to get from all task items
        if (this.onePageTasks.length > 0) {
            endIndex = this._taskList.findIndex(task => task.id === this.onePageTasks[0].id);
            endIndex = endIndex - 1;
        }
        // set the start index to get from all task items
        const startIndex = endIndex - this._numOnceGetFromTaskList > 0 ? endIndex - this._numOnceGetFromTaskList : 0;
        // get the fixed number items from all task items
        const tmp = this._taskList.slice(startIndex, endIndex);
        // stop to push the data to task list ui container if scroll to the top
        if (this.onePageTasks[0].id !== this._taskList[0].id) {
            // reverse the items
            const reverseTmp = tmp.reverse();
            reverseTmp.forEach(item => {
                // keep the task ui container size as this._numShowInTaskListContainer = 40
                if (this.onePageTasks.length === this._numShowInTaskListContainer) {
                    // remove one item from the end if the number of the container is more than this._numShowInTaskListContainer = 40
                    this.onePageTasks.pop();
                }
                // push one item to the top after remove one
                this.onePageTasks.unshift(item);
            });
        }
    }

    onTouchstart() {
        this._touchStart = new Date().getTime();
    }

    onTouchend(event, item: TaskItem) {
        if (this._isIPAD && new Date().getTime() - this._touchStart > LONG_TOUCH_INTERVAL) {
            event.clientX = event.pageX;
            event.clientY = event.pageY;
            this.onContextMenu(event, item);
        } else {
            this.onItemSelect(event, item);
        }
    }

    showNoData() {
        if (this._taskList && this._taskList.length === 0) {
            return 'flex';
        } else {
            return 'none';
        }
    }

    trackByFn(index, item) {
        return item.id;
    }

    private _actionMessageHandler(action) {
        if (action.type === WorkflowTaskActionTypes.DELETE_TASK_SUCCESS) {
            const taskName = action.payload;
            const msg = 'Task ' + taskName + ' deleted successfully.';
            this._toast.notify({
                message: msg,
                style: NotificationStyles.Success
            });

            const status = this._storeQuerier.queryBySelector(workflowSetTaskStatusSelector);
            const processor = this._storeQuerier.queryBySelector(workflowSetTaskProcessorSelector);
            this._store.dispatch({
                type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS,
                payload: {
                    status: status,
                    processor: processor
                }
            });

            const event = {
                type: 'AutoChange'
            };
            this._slideSheetService.showSlideSheetForTaskList(event);
        }
    }

    private _deleteTask(task) {
        const content: string[] = [];
        if (this._processor === Processor.CreatedByMe) {
            content.push('Are you sure you want to delete this task?');
            content.push('All sub tasks will also be deleted.');
        } else {
            content.push('Are you sure you want to delete this task?');
        }
        const cancelBtn: AlertButton = {
            text: 'Cancel',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Delete',
            type: AlertBtnTypes.destructive
        };

        const subscription = this._alertWindow.
            custom('Delete confirmation required', content, cancelBtn, confirmButton, 500, 'slideSheet', true)
            .subscribe((result: boolean) => {
                if (result) {
                    this._store.dispatch({
                        type: WorkflowTaskActionTypes.DELETE_TASK,
                        payload: {
                            task: task,
                            type: this._processor
                        }
                    });
                }
                subscription.unsubscribe();
            });
    }

    private _initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.single = true;
        this.contextMenuConfig.showItemIcon = false;
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.clickOnDiv = true;
        this.contextMenuConfig.eventKey = 'slideSheet';
        this.contextMenuConfig.eventValue = true;
        this.contextMenuConfig.items = [
            { id: '0', text: 'Delete', disabled: false },
        ];
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
    }

    private _initControls() {
        // set task status menu
        this._status = this._storeQuerier.queryBySelector(workflowSetTaskStatusSelector);
        if (this._status !== undefined) {
            this.taskStatusConfig.selectedItemIndex = this._status;
            this.taskStatusConfig.actionSubject$.next(this.taskStatusConfig);
        }
        // set task processor menu
        this._processor = this._storeQuerier.queryBySelector(workflowSetTaskProcessorSelector);
        if (this._processor !== undefined) {
            this.taskProcessorConfig.selectedItemIndex = this._processor;
            this.taskProcessorConfig.actionSubject$.next(this.taskProcessorConfig);
        }

        this._initContextMenu();
    }

    private _onMenuSelect(event) {
        const task = event.payload.data;
        const type = event.type;
        switch (type) {
            case 0:
                this._deleteTask(task);
                break;
            default:
                break;
        }
    }


    private _onSubscribeTaskList(taskList: Array<TaskItem>) {
        this._taskList = taskList;

        if (this._taskList) {
            const tmp = this._taskList.slice(0, this._numOnceGetFromTaskList);
            this.onePageTasks = tmp;
        }

        setTimeout(() => {
            this.showFilter = true;
        }, 400);
    }

    private _taskStatusEventHandler(event) {
        if (event.type === 'CHANGE') {
            // set store for task status
            this._status = event.payload.status.id;
            this._store.dispatch({
                type: WorkflowTaskActionTypes.SET_TASK_STATUS,
                payload: event.payload.status.id
            });
            // get task proecssor from store
            this._processor = this._storeQuerier.queryBySelector(workflowSetTaskProcessorSelector);
            if (this._processor === null || this._processor === undefined) {
                this._processor = Processor.AssignedToMe;
                this._store.dispatch({
                    type: WorkflowTaskActionTypes.SET_TASK_PROCESSOR,
                    payload: this._processor
                });
            }

            this._store.dispatch({
                type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS,
                payload: {
                    status: this._status,
                    processor: this._processor
                }
            });
        }
    }

    private _taskProcessorEventHandler(event) {
        if (event.type === 'CHANGE') {
            // set store for task processor
            this._processor = event.payload.status.id;
            this._store.dispatch({
                type: WorkflowTaskActionTypes.SET_TASK_PROCESSOR,
                payload: event.payload.status.id
            });
            // get task status from store
            this._status = this._storeQuerier.queryBySelector(workflowSetTaskStatusSelector);
            if (this._status === null || this._status === undefined) {
                this._status = TaskStatusFilter.Ongoing;
                this._store.dispatch({
                    type: WorkflowTaskActionTypes.SET_TASK_STATUS,
                    payload: this._status
                });
            }

            this._store.dispatch({
                type: WorkflowTaskActionTypes.GET_TASK_BY_STATUS,
                payload: {
                    status: this._status,
                    processor: this._processor
                }
            });
        }
    }
}
